/**
 * Pure substring matcher backing the file-tree "speed search"
 * (IntelliJ-style type-to-highlight). Framework-agnostic — no DOM, no React —
 * so it can be unit-tested in isolation.
 *
 * Matching rule (spec §2):
 *   - Case-insensitive "contains" (substring).
 *   - Returns the half-open `[start, end)` range of the FIRST occurrence in
 *     `name`, indexing into the ORIGINAL `name` (not its lowercased form), so
 *     callers can slice the original string for highlighting.
 *   - An empty query (or empty/whitespace-only after no trimming — we treat a
 *     literal empty string as "inactive") returns `null`.
 *   - No fuzzy / regex / camel-hump matching.
 */

/**
 * Find the first case-insensitive occurrence of `query` inside `name`.
 *
 * @returns the `[start, end)` range (end exclusive) of the match in `name`, or
 *   `null` when the query is empty or there is no match. `end - start` always
 *   equals `query.length`.
 */
export function matchRange(name: string, query: string): [number, number] | null {
  // An empty query is "inactive" — nothing to highlight.
  if (query.length === 0) return null;
  // Case-insensitive search. `toLowerCase()` preserves length for the BMP
  // characters that show up in file names (ASCII, accented Latin, etc.), so
  // indices map 1:1 back onto the original `name`. We index against the
  // original `name`'s length implicitly via the lowercased copies, which keep
  // the same code-unit count.
  const haystack = name.toLowerCase();
  const needle = query.toLowerCase();
  const start = haystack.indexOf(needle);
  if (start < 0) return null;
  return [start, start + needle.length];
}
